import {
  FormState,
  formValueSelector,
  getFormMeta,
  getFormSyncErrors,
  getFormValues,
} from 'redux-form';
import get from 'lodash-es/get';
import isNil from 'lodash-es/isNil';

import moment from 'moment';
import { createSelector } from 'reselect';

import { RootState } from 'app/store/rootReducer';
import {
  FieldsEnum,
  FormsEnum,
  LabelEnum,
  ValidationErrorMessagesEnum,
} from 'app/shared/enums';
import { OfferFiltersFormInterface } from './state.interfaces';
import { TravelOfferFiltersFormInterface } from './state.interfaces';
import { Address } from 'app/shared/interfaces/Address.interface';
import { getBirthdayStringByPesel } from 'app/services/pesel.service';
import { RegisterFormValues } from 'app/components/RegisterForm/RegisterForm.interfaces';
import { PasswordChangeFormValues } from 'app/components/PasswordChangeModal/PasswordChangeModal.interfaces';
import { CalculationDataByKeysInterface } from 'app/shared/interfaces/CalculationDataByKeys.interface';

export const getFormVal = <T>(state: RootState, formName: FormsEnum) =>
  getFormValues(formName)(state) as T;

const offerFiltersFormValuesSelector = getFormValues(FormsEnum.OfferFilters);

export const getFilters = (
  state: RootState
): OfferFiltersFormInterface | undefined =>
  offerFiltersFormValuesSelector(state) as OfferFiltersFormInterface;

export const getTravelFilters = (
  state: RootState
): TravelOfferFiltersFormInterface | undefined =>
  getFormValues(FormsEnum.TravelResultsFilters)(
    state
  ) as TravelOfferFiltersFormInterface;

const calculationFormSelector = formValueSelector(FormsEnum.CalculationForm);

export const selectCalculationOwner = (
  state: RootState
): CalculationDataByKeysInterface['owner'] =>
  calculationFormSelector(state, 'owner');

export const selectCalculationCoowners = (
  state: RootState
): CalculationDataByKeysInterface['coowners'] =>
  calculationFormSelector(state, 'coowners');

export const selectCalculationCoownersCount = (
  state: RootState
): CalculationDataByKeysInterface['car']['coownersCount'] =>
  calculationFormSelector(state, 'car.coownersCount');

const checkoutFormSelector = formValueSelector(FormsEnum.CheckoutForm);

const getAddressFromCheckoutForm = (state: RootState, path: string) => {
  return (checkoutFormSelector(state, path) || {}) as Address;
};

export const getCheckoutOwnerLegalAddress = (state: RootState): Address =>
  getAddressFromCheckoutForm(state, 'owner.legalAddress');

export const getCheckoutOwnerHomeAddress = (state: RootState): Address =>
  getAddressFromCheckoutForm(state, 'owner.homeAddress');

export const getCheckoutCoowner1LegalAddress = (state: RootState): Address =>
  getAddressFromCheckoutForm(state, 'coowners[1].legalAddress');

export const getCheckoutCoowner1HomeAddress = (state: RootState): Address =>
  getAddressFromCheckoutForm(state, 'coowners[1].homeAddress');

export const getCheckoutCoowner2LegalAddress = (state: RootState): Address =>
  getAddressFromCheckoutForm(state, 'coowners[2].legalAddress');

export const getCheckoutCoowner2HomeAddress = (state: RootState): Address =>
  getAddressFromCheckoutForm(state, 'coowners[2].homeAddress');

export const getParamValues = (
  state: RootState,
  formName: FormsEnum,
  paramNames: Array<string>
) => {
  const formValues = getFormValues(formName)(state);
  const calculationFormValues = getFormValues(FormsEnum.CalculationForm)(state);
  const paramValues: { [key: string]: string } = {};

  paramNames.forEach((paramName) => {
    if (paramName.includes(':')) {
      const [requestParamName, formParamName] = paramName.split(':');
      paramValues[requestParamName] = get(formValues, formParamName);
      if (
        formName === FormsEnum.CompareCepikDataForm &&
        (!paramValues[requestParamName] || isNil(paramValues[requestParamName]))
      ) {
        paramValues[requestParamName] = get(
          calculationFormValues,
          formParamName
        );
      }
    } else {
      paramValues[paramName] = get(formValues, paramName);
      if (
        formName === FormsEnum.CompareCepikDataForm &&
        (!paramValues[paramName] || isNil(paramValues[paramName]))
      ) {
        paramValues[paramName] = get(calculationFormValues, paramName);
      }
    }
  });

  if (
    Object.entries(paramValues).some(
      (value) => value === undefined || value === null
    )
  ) {
    return '{}';
  }
  return JSON.stringify(paramValues);
};

const getIsAdultByPesel = (state: RootState, peselField: string) => {
  const pesel = checkoutFormSelector(state, peselField);

  if (!pesel) {
    return false;
  }

  const birthdayString = getBirthdayStringByPesel(pesel);

  if (!birthdayString) {
    return false;
  }

  const birthday = moment(birthdayString);
  const today = moment();
  const diffInYears = today.diff(birthday, 'years');

  return diffInYears >= 18;
};

export const isOwnerAdult = (state: RootState) =>
  getIsAdultByPesel(state, 'owner.peselCheckoutForm');

export const isCoowner1Adult = (state: RootState) =>
  getIsAdultByPesel(state, 'coowners[1].peselCheckoutForm');

export const isCoowner2Adult = (state: RootState) =>
  getIsAdultByPesel(state, 'coowners[2].peselCheckoutForm');

const getFormState = (state: RootState) => state.form;

const getNamedFormState = (formName: FormsEnum) =>
  createSelector([getFormState], (formState = {}) => formState[formName]);

export const registeredFieldsSelector = (formName: FormsEnum) =>
  createSelector(
    [getNamedFormState(formName)],
    (namedFormState = {}): FormState['registeredFields'] =>
      namedFormState.registeredFields
  );

const selectRegisterFormPasswordError = (state: RootState) =>
  (getFormSyncErrors(FormsEnum.RegisterForm)(state) as RegisterFormValues)?.[
    FieldsEnum.RegisterPassword
  ];

const selectRegisterFormPasswordMeta = (state: RootState) =>
  (getFormMeta(FormsEnum.RegisterForm)(state) as Record<
    string,
    { touched: boolean; valid: boolean }
  >)?.[FieldsEnum.RegisterPassword];

export const selectShouldHideRegisterFormPasswordHint = createSelector(
  [selectRegisterFormPasswordError, selectRegisterFormPasswordMeta],
  (passwordError, passwordMeta) => {
    const isPasswordErrorVisible =
      passwordMeta?.touched && !passwordMeta?.valid;

    return Boolean(
      isPasswordErrorVisible &&
        (passwordError ===
          ValidationErrorMessagesEnum.PasswordWhitespaceError ||
          passwordError === LabelEnum.PasswordHint)
    );
  }
);

const selectPasswordChangeFormPasswordError = (state: RootState) =>
  (getFormSyncErrors(FormsEnum.PasswordChangeForm)(
    state
  ) as PasswordChangeFormValues)?.[FieldsEnum.NewPassword];

const selectPasswordChangeFormPasswordMeta = (state: RootState) =>
  (getFormMeta(FormsEnum.PasswordChangeForm)(state) as Record<
    string,
    { touched: boolean; valid: boolean }
  >)?.[FieldsEnum.NewPassword];

export const selectShouldHidePasswordChangeFormPasswordHint = createSelector(
  [selectPasswordChangeFormPasswordError, selectPasswordChangeFormPasswordMeta],
  (passwordError, passwordMeta) => {
    const isPasswordErrorVisible =
      passwordMeta?.touched && !passwordMeta?.valid;

    return Boolean(
      isPasswordErrorVisible &&
        (passwordError ===
          ValidationErrorMessagesEnum.PasswordWhitespaceError ||
          passwordError === LabelEnum.PasswordHint)
    );
  }
);
